import { DarkModePreferenceSetter } from './darkModeTypes'
import { DarkMode } from './types'

/**
 * Updates dark mode styles on the document
 * @param darkMode - true for dark mode, false for light mode, undefined for auto
 * @param applyBackground - Whether to apply dark mode data attribute (only for themes that support dark mode)
 *
 * When applyBackground is true (Pegasus):
 *   - Sets data-dark-mode attribute to enable CSS variable switching
 *
 * When applyBackground is false (Narwhal/Unicorn):
 *   - Removes data-dark-mode attribute to allow theme-specific backgrounds
 */
export function updateDarkModeStyles(darkMode: boolean | undefined, applyBackground: boolean = true): void {
  if (typeof window === 'undefined') return

  const htmlElement = document.documentElement

  if (applyBackground) {
    const isDarkModeActive = darkMode === true

    const currentAttr = htmlElement.getAttribute('data-dark-mode')

    if (currentAttr !== isDarkModeActive.toString()) {
      htmlElement.setAttribute('data-dark-mode', isDarkModeActive.toString())

      // Clear any inline background styles to let CSS variables take effect
      htmlElement.style.removeProperty('background-color')
      document.body?.style.removeProperty('background-color')
    }
  } else {
    htmlElement.removeAttribute('data-dark-mode')
    // Clear any inline background styles for non-dark-mode themes
    htmlElement.style.removeProperty('background-color')
    document.body?.style.removeProperty('background-color')
  }
}

/**
 * Syncs dark mode preference via provided setter function
 * @param darkMode - system, dark, light
 * @param setPreference - Function to persist preference (e.g., cookie setter from app)
 */
export function syncDarkModeCookie(darkMode: DarkMode, setPreference?: DarkModePreferenceSetter): void {
  if (!setPreference) return

  const preference = darkMode === DarkMode.System ? 'system' : darkMode === DarkMode.Dark ? 'dark' : 'light'
  setPreference(preference)
}
